import { useState } from 'react';

import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowUpDown, FileText } from 'lucide-react';

import { RoleSelect } from './RoleSelect';
import { useUpdateUser } from '@/api/mutations/useUpdateUser';
import useFetchOrgUsersPaginated from '@/api/queries/analyticsQueries/useFetchOrgUsersPaginated';
import { useGetAllOrganisationUserRoles } from '@/api/queries/useGetOrganisationUserRoles';
import { UserWithMetadata, UserRole } from '@/api/types/backend-dto-types';
import { Button } from '@/components/atoms/new-button';
import TooltipIcon from '@/components/atoms/TooltipIcon';
import SearchBox from '@/components/molecules/SearchBox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/molecules/table';

interface UsersTableProps {
  orgId: string;
  showViewMatters?: boolean;
  isGlobalAdminUserTable?: boolean;
  onViewUserMatters: (user: UserWithMetadata) => void;
}

const UsersTable = ({
  orgId,
  showViewMatters = true,
  isGlobalAdminUserTable = false,
  onViewUserMatters,
}: UsersTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Fetch users with pagination
  const { data: usersResponse, isFetching: isLoading } = useFetchOrgUsersPaginated({
    orgIds: orgId ? [orgId] : [],
    includeMetadata: true,
    page: currentPage,
    pageSize,
    searchQuery,
  });

  const users = usersResponse?.data ?? [];
  const totalPages = usersResponse?.pageCount ?? 1;
  const totalCount = usersResponse?.totalCount ?? 0;

  // Search handlers
  const handleSearch = (query: string) => {
    setSearchQuery(query.trim());
    setCurrentPage(1); // Reset to first page when searching
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  const { data: roles } = useGetAllOrganisationUserRoles(orgId);
  const { mutate: updateUser } = useUpdateUser();

  // Define columns for the table
  const columns: ColumnDef<UserWithMetadata>[] = [
    {
      accessorKey: 'email',
      header: ({ column }) => {
        return (
          <div className="text-left">
            <div
              className="flex items-center gap-1 hover:cursor-pointer"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            >
              Email
              <ArrowUpDown className="h-4 w-4 text-gray-700" />
            </div>
          </div>
        );
      },
      cell: ({ row }) => {
        const email = row.getValue('email') as string;
        const disabled = row.original.disabled;
        return (
          <div className="text-left">
            {email}
            {disabled ? ' (disabled)' : ''}
          </div>
        );
      },
    },
    {
      accessorKey: 'role',
      header: () => {
        return (
          <div className="text-left">
            <div className="flex items-center gap-1">Role</div>
          </div>
        );
      },
      cell: ({ row }) => {
        const role = row.getValue<UserRole>('role');

        return (
          <div className="text-left" data-testid={`user-role-${row.original.id}`}>
            {(!isGlobalAdminUserTable === true && role.name === 'Administrator') || row.original.disabled ? (
              <span>{role.name}</span>
            ) : (
              <RoleSelect
                roles={roles ?? []}
                role={role}
                updateRole={(roleId) => {
                  updateUser({
                    userId: row.original.id,
                    roleId,
                  });
                }}
              />
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'hasBeenActive',
      header: ({ column }) => {
        return (
          <div className="text-left">
            <div
              className="flex items-center gap-1 hover:cursor-pointer"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            >
              <TooltipIcon tooltipContent="Whether the user has completed all onboarding steps." />
              Onboarded
              <ArrowUpDown className="h-4 w-4 text-gray-700" />
            </div>
          </div>
        );
      },
      cell: ({ row }) => {
        const hasBeenActive = row.getValue('hasBeenActive') as boolean;
        return (
          <span
            className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
              hasBeenActive ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
            }`}
          >
            {hasBeenActive ? 'Yes' : 'No'}
          </span>
        );
      },
    },
    {
      accessorKey: 'lastActiveAt',
      accessorFn: (row) => row.metadata?.lastActiveAt ?? 0,
      header: ({ column }) => {
        return (
          <div className="text-left">
            <div
              className="flex items-center gap-1 hover:cursor-pointer"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            >
              Last Online
              <ArrowUpDown className="h-4 w-4 text-gray-700" />
            </div>
          </div>
        );
      },
      cell: ({ row }) => {
        const user = row.original;
        const lastActiveAt = user.metadata?.lastActiveAt;
        return (
          <span className="inline-flex rounded-full px-2 py-1 text-xs font-medium">
            {lastActiveAt
              ? new Date(lastActiveAt * 1000).toLocaleString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : 'Never'}
          </span>
        );
      },
    },
    {
      accessorKey: 'matters',
      header: ({ column }) => {
        return (
          <div className="text-left">
            <div
              className="flex items-center gap-1 hover:cursor-pointer"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            >
              Matters
              <ArrowUpDown className="h-4 w-4 text-gray-700" />
            </div>
          </div>
        );
      },
      cell: ({ row }) => {
        const user = row.original;
        const matters = user.matters?.length ?? 0;

        return (
          <div className="flex items-center gap-2">
            {/* -1 to exclude personal Matters */}
            <span className="inline-flex rounded-full px-2 py-1 text-xs font-medium">{matters - 1}</span>

            {matters > 0 && showViewMatters && (
              <Button
                onClick={() => onViewUserMatters(user)}
                className="flex h-auto items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 shadow-sm transition-all hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600"
                variant="ghost"
                aria-label="View user matters"
              >
                <FileText className="h-3.5 w-3.5" />
                <span className="text-xs font-medium">View</span>
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: users || [],
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    manualPagination: true,
    pageCount: totalPages,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
    },
  });

  return (
    <div className="flex w-full flex-col justify-center gap-4 rounded-xl bg-white p-6 shadow-sm">
      <div className="flex w-full flex-row items-center justify-between gap-2">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold text-gray-800">All Users</h2>
        </div>

        <SearchBox
          placeholder="Search by email..."
          value={searchInput}
          onChange={setSearchInput}
          onSearchCall={handleSearch}
          clearSearch={clearSearch}
        />
      </div>

      <div className="w-full overflow-x-auto rounded-xl border border-gray-200 text-sm">
        {isLoading ? (
          <div className="flex h-60 items-center justify-center p-8">
            <FontAwesomeIcon icon={faCircleNotch} className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row, index) => {
                    const disabled = row.original.disabled;
                    return (
                      <TableRow
                        key={row.id}
                        className={`h-16 ${disabled ? 'bg-red-50 hover:bg-red-100' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center text-gray-500">
                      No users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between space-x-2 py-4 text-gray-700">
        <div className="flex-1 text-sm">
          Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount}{' '}
          users
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span className="px-3 py-1 text-sm">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UsersTable;
